import { useState } from "react";
import { useRoom } from "../contexts/RoomContext.jsx";
import { STOP_CATEGORIES } from "../data/stopCategories.js";
import { progressoVotacaoStop } from "../domain/stop.js";

export function StopVotingPage() {
  const { user, roomData, isHost, votarRespostaStop, apurarResultadoStop } =
    useRoom();
  const [categoryIndex, setCategoryIndex] = useState(0);
  const players = Object.values(roomData?.players || {});
  const answers = roomData?.stopRespostas || {};
  const votes = roomData?.stopVotos || {};
  const letter = roomData?.stopLetraAtual || "";
  const stoppedById = roomData?.stopParadoPor;
  const stoppedBy = stoppedById
    ? roomData?.players?.[stoppedById]?.username
    : null;
  const category = STOP_CATEGORIES[categoryIndex] || STOP_CATEGORIES[0];
  const progressFor = (categoryId) =>
    progressoVotacaoStop(players, votes, categoryId);
  const totalNeeded =
    players.length * Math.max(players.length - 1, 0) * STOP_CATEGORIES.length;
  const totalReceived = STOP_CATEGORIES.reduce(
    (total, item) => total + progressFor(item.id).received,
    0,
  );
  const progress = progressFor(category.id);
  const navigate = (direction) =>
    setCategoryIndex(
      (index) =>
        (index + direction + STOP_CATEGORIES.length) % STOP_CATEGORIES.length,
    );

  return (
    <div className="container stop-voting-page">
      <div className="text-center stop-vote-heading">
        <h2 className="title">🗳️ Votação coletiva</h2>
        <p className="subtitle">
          {stoppedBy ? `${stoppedBy} gritou STOP! ` : "O tempo acabou! "}
          Letra <strong>{letter}</strong> — todos veem esta mesma tabela em
          tempo real.
        </p>
        <p className="stop-vote-total">
          {totalReceived}/{totalNeeded} votos registrados
        </p>
      </div>

      <nav
        className="stop-vote-category-tabs"
        aria-label="Categorias da votação"
      >
        {STOP_CATEGORIES.map((item, index) => {
          const itemProgress = progressFor(item.id);
          return (
            <button
              aria-current={index === categoryIndex ? "page" : undefined}
              className={`stop-vote-category-tab${index === categoryIndex ? " active" : ""}${itemProgress.complete ? " complete" : ""}`}
              key={item.id}
              onClick={() => setCategoryIndex(index)}
              type="button"
            >
              <span>
                {item.emoji} {item.label}
              </span>
              <small>
                {itemProgress.complete
                  ? "✓ completa"
                  : `${itemProgress.received}/${itemProgress.needed}`}
              </small>
            </button>
          );
        })}
      </nav>

      <section className="card stop-vote-card">
        <div className="stop-vote-category-heading">
          <button
            aria-label="Categoria anterior"
            className="stop-vote-nav"
            onClick={() => navigate(-1)}
            type="button"
          >
            ‹
          </button>
          <div>
            <h3>
              {category.emoji} {category.label}
            </h3>
            {category.descricao && <p>{category.descricao}</p>}
          </div>
          <button
            aria-label="Próxima categoria"
            className="stop-vote-nav"
            onClick={() => navigate(1)}
            type="button"
          >
            ›
          </button>
        </div>
        <div
          className="stop-vote-progress"
          aria-label={`Progresso: ${progress.received} de ${progress.needed} votos`}
        >
          <span
            style={{
              width: `${progress.needed ? (progress.received / progress.needed) * 100 : 0}%`,
            }}
          />
        </div>
        <div className="stop-vote-table-wrap">
          <table className="stop-vote-table">
            <thead>
              <tr>
                <th className="stop-vote-answer-column">Resposta</th>
                {players.map((player) => (
                  <th title={player.username} key={player.id}>
                    <span className="stop-vote-voter-name">
                      {player.username}
                    </span>
                  </th>
                ))}
                <th className="stop-vote-actions-column">Seu voto</th>
              </tr>
            </thead>
            <tbody>
              {players.map((target) => {
                const answer = (answers[target.id]?.[category.id] || "").trim();
                const ownAnswer = target.id === user?.uid;
                const ownVote = votes?.[target.id]?.[category.id]?.[user?.uid];
                return (
                  <tr key={target.id}>
                    <th scope="row" className="stop-vote-answer-column">
                      <small>
                        {target.username}
                        {ownAnswer ? " (você)" : ""}
                      </small>
                      <strong className={answer ? "" : "empty"}>
                        {answer || "em branco"}
                      </strong>
                    </th>
                    {players.map((voter) => {
                      const vote =
                        votes?.[target.id]?.[category.id]?.[voter.id];
                      const voterOwnAnswer = voter.id === target.id;
                      const label = voterOwnAnswer
                        ? "não vota na própria resposta"
                        : vote === true
                          ? "votou válida"
                          : vote === false
                            ? "votou inválida"
                            : "ainda não votou";
                      return (
                        <td
                          className={`stop-vote-public-cell ${voterOwnAnswer ? "own" : ""}`}
                          key={voter.id}
                          title={`${voter.username}: ${label}`}
                        >
                          <span aria-label={label}>
                            {voterOwnAnswer
                              ? "—"
                              : vote === true
                                ? "✅"
                                : vote === false
                                  ? "❌"
                                  : "⏳"}
                          </span>
                        </td>
                      );
                    })}
                    <td className="stop-vote-actions-column">
                      {ownAnswer ? (
                        <span className="stop-vote-own-answer">
                          Sua resposta
                        </span>
                      ) : (
                        <div className="stop-vote-actions">
                          <button
                            aria-label={`Marcar resposta de ${target.username} como válida`}
                            className={`valid${ownVote === true ? " selected" : ""}`}
                            onClick={() =>
                              votarRespostaStop(target.id, category.id, true)
                            }
                            title="Marcar como válida"
                            type="button"
                          >
                            ✅
                          </button>
                          <button
                            aria-label={`Marcar resposta de ${target.username} como inválida`}
                            className={`invalid${ownVote === false ? " selected" : ""}`}
                            onClick={() =>
                              votarRespostaStop(target.id, category.id, false)
                            }
                            title="Marcar como inválida"
                            type="button"
                          >
                            ❌
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="stop-vote-legend">
          ✅ válida · ❌ inválida · ⏳ aguardando · — própria resposta
        </p>
      </section>
      <div className="mt-40 text-center">
        {isHost ? (
          <button
            className="btn btn-success"
            onClick={apurarResultadoStop}
            type="button"
          >
            📊 Encerrar votação e apurar
          </button>
        ) : (
          <p className="info-box">
            Aguardando o host apurar o resultado da rodada...
          </p>
        )}
      </div>
    </div>
  );
}
